import * as React from 'react';
import { useStyles, useMergedRef } from '@sravni/react-utils';
import modules_0b64ef5e from '../styles/Sheet.module.less.js';
import SheetContext from './SheetContext.js';

/**
 * @description Sheet Content component.
 *
 * @component
 * @example
 * ```jsx
 * <Sheet.Content>content</Sheet>
 * ```
 */
const Content = React.forwardRef((props, ref) => {
    const { className, children, ...restProps } = props;
    const { setDragElastic } = React.useContext(SheetContext);
    const containerRef = React.useRef(null);
    const allowUp = React.useRef(false);
    const allowDown = React.useRef(false);
    const prevPageY = React.useRef(0);
    const prevPageX = React.useRef(0);
    const [dividerVisible, setDividerVisible] = React.useState(false);
    const cx = useStyles(modules_0b64ef5e);
    const classNames = cx(className, 'content', {
        divider: dividerVisible,
    });
    const updateDivider = React.useCallback(() => {
        const container = containerRef?.current;
        if (!container)
            return;
        setDividerVisible(container.scrollHeight > container.clientHeight);
    }, [containerRef]);
    const handleTouchStart = React.useCallback((event) => {
        const container = containerRef.current;
        if (!container)
            return;
        allowUp.current = container.scrollTop > 0;
        allowDown.current = container.scrollTop < container.scrollHeight - container.clientHeight;
        prevPageY.current = event.touches[0].pageY;
        prevPageX.current = event.touches[0].pageX;
        if (setDragElastic) {
            allowUp.current && !allowDown.current && setDragElastic({ top: 0.5, bottom: 0 });
            !allowUp.current && allowDown.current && setDragElastic({ top: 0, bottom: 0.5 });
            allowUp.current && allowDown.current && setDragElastic({ top: 0, bottom: 0 });
        }
    }, [setDragElastic]);
    const handleTouchMove = React.useCallback((event) => {
        const moveUp = event.touches[0].pageY > prevPageY.current;
        const moveDown = event.touches[0].pageY < prevPageY.current;
        const horizontalMove = event.touches[0].pageX > prevPageX.current || event.touches[0].pageX < prevPageX.current;
        prevPageY.current = event.touches[0].pageY;
        prevPageX.current = event.touches[0].pageX;
        if ((moveUp && allowUp.current) || (moveDown && allowDown.current)) {
            event.stopPropagation();
            return;
        }
        if (horizontalMove && !moveUp && !moveDown)
            return;
        event.preventDefault();
    }, [prevPageY, prevPageX, allowUp, allowDown]);
    const stopPropagationPointerDown = React.useCallback((event) => event.stopPropagation(), []);
    React.useEffect(() => {
        updateDivider();
        window.addEventListener('resize', updateDivider);
        return () => window.removeEventListener('resize', updateDivider);
    }, [updateDivider]);
    React.useEffect(() => {
        const container = containerRef.current;
        container?.addEventListener('touchstart', handleTouchStart);
        container?.addEventListener('touchmove', handleTouchMove);
        return () => {
            container?.removeEventListener('touchstart', handleTouchStart);
            container?.removeEventListener('touchmove', handleTouchMove);
        };
    }, [handleTouchStart, handleTouchMove]);
    return (React.createElement("div", { ...restProps, ref: useMergedRef(ref, containerRef), className: classNames, onPointerDown: stopPropagationPointerDown }, children));
});
Content.defaultProps = {};
Content.displayName = 'Content';

export { Content };
//# sourceMappingURL=Content.js.map
